"use client";

import { Button } from "@/components/ui/button";
import { GripVerticalIcon } from "@/components/ui/grip-vertical";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { UploadIcon } from "@/components/ui/upload";
import type { FileManagerTreeNode } from "@/hooks/use-file-manager-tree";
import { cn } from "@/lib/utils";
import {
  ArrowDownIcon,
  ArrowUpDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  fileTypeLabel,
  formatBytes,
  formatNzDate,
  KB_STATUS_DOT,
  KB_STATUS_LABELS,
  KB_STATUS_TEXT,
  toKbStatus,
  type KbFileRow,
  type KbStatus,
} from "./kb-utils";

/** Rows rendered before the first scroll-triggered page, and per page after. */
const PAGE_SIZE = 15;

export type KbSortKey = "name" | "size";
export type KbSort = { key: KbSortKey; direction: "asc" | "desc" };

type KbFilesTableProps = {
  rows: KbFileRow[];
  /** Changes whenever the team, collection, search or status filter changes,
   *  so paging restarts — a status poll re-creating `rows` must not reset it. */
  resetKey: string;
  /** Files after search + status filtering (used for the footer count). */
  visibleCount: number;
  /** Every file in the current scope (team root, collection, or search). */
  totalCount: number;
  /** True when a search or status filter is narrowing the list. */
  filtered: boolean;
  onShowAll: () => void;
  sort: KbSort | null;
  onSortChange: (key: KbSortKey) => void;
  expandedGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  selectedFileIds: Set<string>;
  onToggleSelection: (nodeId: string) => void;
  canDragFile: (file: FileManagerTreeNode) => boolean;
  onDragStartFiles: (files: FileManagerTreeNode[]) => void;
  onDragEndFiles: () => void;
  renderActions: (file: FileManagerTreeNode) => ReactNode;
  loading: boolean;
  onUpload: () => void;
  canUpload: boolean;
  /** Shown when the current scope holds no files at all. */
  emptyMessage: string;
  uploadLabel: string;
  /** Optional muted second line under the name, e.g. where a search hit lives. */
  locationFor?: (file: FileManagerTreeNode) => string | null;
  /** Opens the file in the side preview panel (clicking the name). */
  onPreview: (file: FileManagerTreeNode) => void;
  /** Entity id of the file whose preview link is being resolved. */
  previewingFileId: string | null;
};

// File name as a link-style button: the row itself toggles selection, so the
// name is the click target for opening the preview.
const PreviewName = ({
  file,
  label,
  loading,
  onPreview,
  className,
}: {
  file: FileManagerTreeNode;
  label: string;
  loading: boolean;
  onPreview: (file: FileManagerTreeNode) => void;
  className?: string;
}) => (
  <button
    type="button"
    title={`Preview ${file.name}`}
    aria-label={`Preview ${file.name}`}
    className={cn(
      "flex max-w-full items-center gap-1.5 rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      className
    )}
    onClick={(event) => {
      event.stopPropagation();
      onPreview(file);
    }}
  >
    <span className="truncate">{label}</span>
    {loading ? <Spinner className="size-3.5 shrink-0 text-muted-foreground" /> : null}
  </button>
);

const SortableHead = ({
  label,
  sortKey,
  sort,
  onSortChange,
  className,
}: {
  label: string;
  sortKey: KbSortKey;
  sort: KbSort | null;
  onSortChange: (key: KbSortKey) => void;
  className?: string;
}) => {
  const isSorted = sort?.key === sortKey;
  const SortIcon = isSorted
    ? sort.direction === "asc"
      ? ArrowUpIcon
      : ArrowDownIcon
    : ArrowUpDownIcon;

  return (
    <TableHead
      className={className}
      aria-sort={isSorted ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSortChange(sortKey)}
        className="group inline-flex items-center gap-1 text-left hover:text-foreground"
      >
        {label}
        <SortIcon
          className={cn(
            "size-3.5 shrink-0",
            isSorted
              ? "text-foreground"
              : "text-muted-foreground/50 group-hover:text-muted-foreground"
          )}
        />
      </button>
    </TableHead>
  );
};

const StatusCell = ({ status, summary }: { status: KbStatus; summary?: string }) => (
  <span className="flex items-center gap-2 whitespace-nowrap">
    <span className={cn("size-2 shrink-0 rounded-full", KB_STATUS_DOT[status])} aria-hidden />
    <span className={cn("text-sm", KB_STATUS_TEXT[status])}>
      {summary ?? KB_STATUS_LABELS[status]}
    </span>
  </span>
);

const DragHandle = ({
  label,
  enabled,
  onDragStart,
  onDragEnd,
}: {
  label: string;
  enabled: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
}) => {
  const handle = (
    <span
      draggable={enabled}
      aria-hidden={!enabled}
      role={enabled ? "button" : undefined}
      aria-label={enabled ? `Drag ${label} to move it` : undefined}
      tabIndex={-1}
      onClick={(event) => event.stopPropagation()}
      onDragStart={(event) => {
        if (!enabled) return;
        event.stopPropagation();
        event.dataTransfer.effectAllowed = "move";
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      className={cn(
        "flex size-7 items-center justify-center rounded-md text-muted-foreground/60",
        enabled
          ? "cursor-grab transition-colors hover:bg-accent hover:text-foreground active:cursor-grabbing"
          : "cursor-not-allowed opacity-40"
      )}
    >
      <GripVerticalIcon size={14} />
    </span>
  );

  if (enabled) return handle;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{handle}</TooltipTrigger>
      <TooltipContent side="right">
        Only the person who uploaded this file can move it to another team.
      </TooltipContent>
    </Tooltip>
  );
};

export function KbFilesTable({
  rows,
  resetKey,
  visibleCount,
  totalCount,
  filtered,
  onShowAll,
  sort,
  onSortChange,
  expandedGroups,
  onToggleGroup,
  selectedFileIds,
  onToggleSelection,
  canDragFile,
  onDragStartFiles,
  onDragEndFiles,
  renderActions,
  loading,
  onUpload,
  canUpload,
  emptyMessage,
  uploadLabel,
  locationFor,
  onPreview,
  previewingFileId,
}: KbFilesTableProps) {
  // Paging state is keyed so a changed filter resets it during render rather
  // than in an effect, which would render one stale page first.
  const [paging, setPaging] = useState({ key: resetKey, limit: PAGE_SIZE, loadingMore: false });
  if (paging.key !== resetKey) {
    setPaging({ key: resetKey, limit: PAGE_SIZE, loadingMore: false });
  }
  const { limit, loadingMore } = paging;
  const setLimit = (next: number | ((current: number) => number)) =>
    setPaging((current) => ({
      ...current,
      limit: typeof next === "function" ? next(current.limit) : next,
      loadingMore: false,
    }));
  const setLoadingMore = (value: boolean) =>
    setPaging((current) => ({ ...current, loadingMore: value }));
  const sentinelRef = useRef<HTMLTableRowElement | null>(null);

  const pagedRows = useMemo(() => rows.slice(0, limit), [rows, limit]);
  const hasMore = rows.length > pagedRows.length;

  // Everything is already in memory, so "loading" is just a rendering beat —
  // the skeleton keeps the jump from feeling like a layout snap.
  useEffect(() => {
    if (!hasMore) return;
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setLoadingMore(true);
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, limit]);

  useEffect(() => {
    if (!loadingMore) return;
    const timer = setTimeout(() => {
      setLimit((current) => current + PAGE_SIZE);
    }, 250);
    return () => clearTimeout(timer);
  }, [loadingMore]);

  // A collapsed duplicate group stands for all of its copies in the count.
  const renderedFileCount = useMemo(
    () =>
      pagedRows.reduce(
        (total, row) => total + (row.kind === "group" ? row.files.length : 1),
        0
      ),
    [pagedRows]
  );

  if (loading) {
    return (
      <p className="rounded-xl border border-border bg-card px-4 py-10 text-center text-sm text-muted-foreground">
        Loading…
      </p>
    );
  }

  if (totalCount === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border bg-card/50 px-4 py-12 text-center">
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        <Button type="button" size="sm" className="gap-1.5" onClick={onUpload} disabled={!canUpload}>
          <UploadIcon className="size-4" size={16} />
          {uploadLabel}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-xl border border-border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-10" />
              <SortableHead
                label="Name"
                sortKey="name"
                sort={sort}
                onSortChange={onSortChange}
              />
              <TableHead className="w-[130px]">Status</TableHead>
              <TableHead className="w-[115px]">Type</TableHead>
              <SortableHead
                label="Size"
                sortKey="size"
                sort={sort}
                onSortChange={onSortChange}
                className="w-[85px]"
              />
              <TableHead className="w-[170px]">Updated</TableHead>
              <TableHead className="w-12 text-right">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                  No files match your search.
                </TableCell>
              </TableRow>
            ) : null}

            {pagedRows.map((row) => {
              if (row.kind === "single") {
                const file = row.file;
                const status = toKbStatus(file.processingStatus);
                const isSelected = selectedFileIds.has(file.id);
                const location = locationFor?.(file);
                return (
                  <TableRow
                    key={row.key}
                    data-state={isSelected ? "selected" : undefined}
                    className="cursor-pointer"
                    onClick={() => onToggleSelection(file.id)}
                  >
                    <TableCell className="pr-0">
                      <DragHandle
                        label={file.name}
                        enabled={canDragFile(file)}
                        onDragStart={() => onDragStartFiles([file])}
                        onDragEnd={onDragEndFiles}
                      />
                    </TableCell>
                    <TableCell className="max-w-0">
                      <PreviewName
                        file={file}
                        label={file.name}
                        loading={previewingFileId === file.entityId}
                        onPreview={onPreview}
                        className="font-medium text-foreground"
                      />
                      {location ? (
                        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                          {location}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <StatusCell status={status} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {fileTypeLabel(file.fileType)}
                    </TableCell>
                    <TableCell className="text-muted-foreground tabular-nums">
                      {file.size ? formatBytes(file.size) : "—"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">
                      {formatNzDate(file.updatedAt ?? file.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">{renderActions(file)}</TableCell>
                  </TableRow>
                );
              }

              const isExpanded = expandedGroups.has(row.key);
              const groupDraggable = row.files.every(canDragFile);

              return [
                <TableRow
                  key={row.key}
                  className="cursor-pointer"
                  onClick={() => onToggleGroup(row.key)}
                >
                  <TableCell className="pr-0">
                    <DragHandle
                      label={`${row.files.length} copies of ${row.name}`}
                      enabled={groupDraggable}
                      onDragStart={() => onDragStartFiles(row.files)}
                      onDragEnd={onDragEndFiles}
                    />
                  </TableCell>
                  <TableCell className="max-w-0">
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        aria-hidden
                        className="flex size-5 shrink-0 items-center justify-center text-muted-foreground"
                      >
                        {isExpanded ? (
                          <ChevronDownIcon className="size-4" />
                        ) : (
                          <ChevronRightIcon className="size-4" />
                        )}
                      </span>
                      <span className="truncate font-medium text-foreground" title={row.name}>
                        {row.name}
                      </span>
                      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                        {row.files.length} copies
                      </span>
                    </span>
                  </TableCell>
                  <TableCell>
                    <StatusCell
                      status={row.status}
                      summary={`${row.readyCount} of ${row.files.length} ready`}
                    />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {fileTypeLabel(row.files[0]?.fileType)}
                  </TableCell>
                  <TableCell className="text-muted-foreground tabular-nums">—</TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">
                    {formatNzDate(row.updatedAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-expanded={isExpanded}
                      aria-label={
                        isExpanded
                          ? `Collapse copies of ${row.name}`
                          : `Expand ${row.files.length} copies of ${row.name}`
                      }
                      className="size-8 text-muted-foreground hover:text-foreground"
                      onClick={(event) => {
                        event.stopPropagation();
                        onToggleGroup(row.key);
                      }}
                    >
                      {isExpanded ? (
                        <ChevronDownIcon className="size-4" />
                      ) : (
                        <ChevronRightIcon className="size-4" />
                      )}
                    </Button>
                  </TableCell>
                </TableRow>,
                ...(isExpanded
                  ? row.files.map((file, index) => {
                      const status = toKbStatus(file.processingStatus);
                      const isSelected = selectedFileIds.has(file.id);
                      return (
                        <TableRow
                          key={`${row.key}:${file.id}`}
                          data-state={isSelected ? "selected" : undefined}
                          className="cursor-pointer bg-muted/20"
                          onClick={() => onToggleSelection(file.id)}
                        >
                          <TableCell className="pr-0">
                            <DragHandle
                              label={`copy ${index + 1} of ${file.name}`}
                              enabled={canDragFile(file)}
                              onDragStart={() => onDragStartFiles([file])}
                              onDragEnd={onDragEndFiles}
                            />
                          </TableCell>
                          <TableCell className="max-w-0 pl-10">
                            <PreviewName
                              file={file}
                              label={`Copy ${index + 1}`}
                              loading={previewingFileId === file.entityId}
                              onPreview={onPreview}
                              className="text-muted-foreground"
                            />
                          </TableCell>
                          <TableCell>
                            <StatusCell status={status} />
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {fileTypeLabel(file.fileType)}
                          </TableCell>
                          <TableCell className="text-muted-foreground tabular-nums">
                            {file.size ? formatBytes(file.size) : "—"}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">
                            {formatNzDate(file.updatedAt ?? file.createdAt)}
                          </TableCell>
                          <TableCell className="text-right">{renderActions(file)}</TableCell>
                        </TableRow>
                      );
                    })
                  : []),
              ];
            })}

            {hasMore ? (
              <TableRow ref={sentinelRef} className="hover:bg-transparent">
                <TableCell colSpan={7} className="p-0">
                  <span className="sr-only">Loading more files…</span>
                </TableCell>
              </TableRow>
            ) : null}

            {loadingMore
              ? Array.from({ length: Math.min(PAGE_SIZE, rows.length - pagedRows.length) }).map(
                  (_, index) => (
                    <TableRow key={`skeleton:${index}`} className="hover:bg-transparent">
                      <TableCell className="pr-0">
                        <Skeleton className="size-4 rounded" />
                      </TableCell>
                      <TableCell className="max-w-0">
                        <Skeleton className="h-4 w-3/4 rounded" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20 rounded" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-16 rounded" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-12 rounded" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-28 rounded" />
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  )
                )
              : null}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <p className="text-xs text-muted-foreground tabular-nums">
          Showing {Math.min(renderedFileCount, visibleCount)} of {totalCount} files
        </p>
        {filtered || hasMore ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => {
              setLimit(rows.length);
              onShowAll();
            }}
          >
            Show all files
          </Button>
        ) : null}
      </div>
    </div>
  );
}
